import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ProgressColourPalette } from "@app/directives/_misc/v-progress-bar/v-progress-bar.component";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { IVehicleWithCameraMappings } from "@app/main/components/resourceManagement/cameras/camera-vehicles-manager/camera-vehicles-manager.component";
import { CameraChannelType } from "@app/main/components/videos/enums/CameraChannelType";
import { ICameraChannel } from "@app/main/components/videos/models/ICameraChannel";
import { CameraOnlineHistoryService } from "@app/main/components/videos/services/camera-online-history.service";
import { CameraStatusService } from "@app/main/components/videos/services/camera-status.service";
import { CameraChannelService } from "@app/main/modules/cameras/_services/camera-channels.service";
import { ICommunicationMetadata, IFtCloudDeviceMetadataInfo } from "@app/main/modules/foundation/_routes/hardware/_models/IFtCloudDeviceMetadataPayload";
import { IPeripheralMetadata } from "@app/main/modules/foundation/_routes/hardware/_models/IPeripheralMetadata";
import { IStreamaxExtendedDeviceInfo } from "@app/main/modules/foundation/_routes/hardware/_models/IStreamaxExtendedDeviceInfo";
import { PeripheralService } from "@app/main/modules/foundation/_services/peripheral.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, forkJoin, from, timer } from "rxjs";
import { filter, finalize, switchMap, take, tap } from "rxjs/operators";
import './streamax-device-details.component.scss';
import { RagCellBehavior } from "@app/directives/_layout/v-table/_components/cells/rag-cell/rag-cell.component";
import { StreamaxDormantState } from "../../_enums/StreamaxDormantState.enum";
import { ConfigAccountGroupService } from "@app/main/modules/foundation/_services/config-account-group.service";
import { ICameraDeviceParameterProfileSettings, StreamaxCameraProfileService } from "@app/services/camera/streamax-camera-profile-service";
import { PreferencesService } from "@app/services/preferences.service";
import { ITimeZoneInfo } from "@app/userPreferences/model/ITimeZoneInfo";
import { ICameraDeviceParameterRecord } from "@app/main/modules/foundation/_models/_camera-configs/CameraConfigModels";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { DeviceProfileService } from "@app/services/device-profile.service";

@UntilDestroy()
@Component({
    selector: 'streamax-device-details',
    template: require('./streamax-device-details.component.html')
})

export class StreamaxDeviceDetail implements OnInit {
    private refreshStatusesTimer = timer(0, 30_000);

    @Input() peripheral: IPeripheral;
    @Output() selectChannelsTab = new EventEmitter<void>();

    StreamaxDormantState = StreamaxDormantState;

    loading: boolean;
    statusesRefreshing: boolean;
    wakingUp: boolean;

    ProgressColourPalette = ProgressColourPalette;

    peripheralMetadata: IPeripheralMetadata;
    streamaxMetadata: IFtCloudDeviceMetadataInfo;
    commsMetadata: ICommunicationMetadata;

    deviceInfo: IStreamaxExtendedDeviceInfo;

    cameraChannels: ICameraChannel[];
    openCameraConfigButtonLabel: string;
    wakeUpCameraButtonLabel: string;

    showMetadataSidebar: boolean;
    showPeripheralDataSideBar: boolean;
    showSimManagerSidebar: boolean;
    showChannelManager: boolean;
    
    showConfigSidebar: boolean;
    timezones: ITimeZoneInfo[] = [];
    latestRecord: ICameraDeviceParameterRecord;
    deviceProfile: ICameraDeviceParameterProfileSettings;
    vehicleType: VehicleType;

    adas = "ADAS";
    dms = "DMS";
    mb = "MB";

    onlineStatusBehavior: RagCellBehavior<IStreamaxExtendedDeviceInfo> = {
        grayFn: (deviceInfo) => !deviceInfo?.IsOnline && deviceInfo?.dormantState === StreamaxDormantState.Unknown,
        blueFn: (deviceInfo) => deviceInfo?.dormantState === StreamaxDormantState.Dormant,
        greenFn: (deviceInfo) => deviceInfo?.IsOnline,
    }

    get onlineStatus(): string {
        const onlineStatusKey = !!this.deviceInfo?.IsOnline ? "Common.Online" : "Common.Offline";
        const onlineStatus = this.translationService.t(onlineStatusKey);

        const dormantState = this.deviceInfo?.dormantState;

        if (!dormantState || dormantState === StreamaxDormantState.Unknown) {
            return onlineStatus;
        }

        const dormantStateKey = `StreamaxDormantState.${dormantState}`;
        const dormantStateTranslated = this.translationService.t(dormantStateKey);

        return `${onlineStatus} (${dormantStateTranslated})`;
    }

    constructor(
        private readonly peripheralService: PeripheralService,
        private readonly cameraChannelService: CameraChannelService,
        private readonly cameraStatusService: CameraStatusService,
        private readonly cameraOnlineHistoryService: CameraOnlineHistoryService,
        private readonly translationService: TranslationService,
        private readonly configAccountGroupService: ConfigAccountGroupService,
        private readonly streamaxCameraProfileService: StreamaxCameraProfileService,
        private readonly preferencesService: PreferencesService
    ) { }

    async ngOnInit(): Promise<void> {
        this.loadData();
        this.loadSidebarData();
        this.startRefreshingStatuses();
    }

    get openCameraConfigDisabled(): boolean {
        return !!this.openCameraConfigButtonLabel;
    }

    get streamaxChannelCount(): number {
        return this.deviceInfo?.ChannelList?.length;
    }

    get allChannelsCount(): number {
        return this.cameraChannels?.length
    }

    get activeChannelCount(): number {
        return this.cameraChannels?.filter(c => c.IsActive).length ?? 0;
    }

    get hasActiveAdasChannel(): boolean {
        return this.cameraChannels?.some(c => c.IsActive && c.CameraChannelType === CameraChannelType.ADASSensor) ?? false;
    }

    get hasActiveDmsChannel(): boolean {
        return this.cameraChannels?.some(c => c.IsActive && c.CameraChannelType === CameraChannelType.DMSSensor) ?? false;
    }

    toggleMetadataSidebar(): void {
        this.showMetadataSidebar = !this.showMetadataSidebar;
    }

    togglePeripheralJsonSidebar(): void {
        this.showPeripheralDataSideBar = !this.showPeripheralDataSideBar;
    }

    openChannelsTab(): void {
        this.selectChannelsTab.emit();
    }

    openCameraConfig(): void {
        from(this.cameraOnlineHistoryService.getCameraOnlineStatus(this.peripheral.vehicleMapping.VehicleId)).pipe(
            take(1),
            tap(status => {
                if (!status?.Online) {
                    this.openCameraConfigButtonLabel = this.translationService.t('FoundationPeripherals.CameraWentOffline');
                }
            }),
            filter(status => status?.Online),
            switchMap(() => this.cameraStatusService.getCameraConfigPageUrl(this.peripheral.Serial, this.streamaxMetadata?.deviceModel)),
            tap(url => {
                window.open(url.Url, "_blank");
            })
        ).subscribe()
    }

    openSimManager(): void {
        this.showSimManagerSidebar = true;
    }

    closeSimManager(): void {
        this.showSimManagerSidebar = false;
    }


    openChannelManager(): void {
        this.showChannelManager = true;
    }

    closeChannelManager(): void {
        this.showChannelManager = false;
    }

    openConfigSidebar(): void {
        this.showConfigSidebar = true;
    }

    closeConfigSidebar(): void {
        this.showConfigSidebar = false;
    }

    onVehicleChannelsSaved(vehicle: IVehicleWithCameraMappings): void {
        this.cameraChannels = vehicle.cameraChannels;
        this.closeChannelManager();
    }

    refreshStatuses(showLoader: boolean = true): void {
        if (showLoader) {
            this.statusesRefreshing = true;
        }

        combineLatest([
            this.cameraStatusService.getDeviceInfo(this.peripheral.Serial),
            timer(500)
        ])
        .pipe(untilDestroyed(this))
        .subscribe(([deviceInfo]) => {
            this.deviceInfo = deviceInfo;
            this.updateWakeUpCameraButtonLabel();
            this.updateOpenCameraConfigButtonLabel();
            this.statusesRefreshing = false;

            if (this.deviceInfo.dormantState === StreamaxDormantState.Awake) {
                this.wakingUp = false;
            }
        });
    }

    wakeUpCamera(): void {
        const serial = this.peripheral.Serial;

        this.cameraStatusService.wakeUpStreamaxCamera(serial)
        .pipe(untilDestroyed(this))
        .subscribe();

        this.wakingUp = true;
    }

    private loadData(): void {
        this.loading = true;
        forkJoin({
            metadata: this.peripheralService.getPeripheralMetadata(this.peripheral.Serial),
            channels: this.cameraChannelService.getCameraChannels({ VehicleId: this.peripheral.vehicleMapping.VehicleId }),
            deviceInfo: this.cameraStatusService.getDeviceInfo(this.peripheral.Serial),
            cameraStatus: this.cameraOnlineHistoryService.getCameraOnlineStatus(this.peripheral.vehicleMapping.VehicleId)
        }).pipe(
            untilDestroyed(this),
            finalize(() => this.loading = false),
            tap(data => {
                this.peripheralMetadata = data.metadata;
                this.streamaxMetadata = !!data.metadata?.DeviceMetadataInfoJson?.length ? JSON.parse(data.metadata.DeviceMetadataInfoJson) as IFtCloudDeviceMetadataInfo : null;
                this.commsMetadata = this.streamaxMetadata?.communication.find(m => !!m.imei);
                this.cameraChannels = data.channels;
                this.deviceInfo = data.deviceInfo;
                
                this.updateOpenCameraConfigButtonLabel();
                this.updateWakeUpCameraButtonLabel();
            }),
        ).subscribe();
    }

    private loadSidebarData(): void {
        const accountId = this.peripheral.vehicleMapping.vehicle.AccountId;
        const serial = this.peripheral.Serial;

        this.configAccountGroupService.getConfigAccountGroups().pipe(
            untilDestroyed(this),
            tap(groups => {
                const config = groups.find(g => g.AccountId === accountId);
                const peripheralRecord = config.PeripheralWithConfigRecord.find(x => x.Peripherals?.Serial === serial)

                this.latestRecord = peripheralRecord.LatestRecord;
            }),
            filter(() => !!this.latestRecord),
            switchMap(() => {
                return combineLatest([
                    this.streamaxCameraProfileService.getProfiles(),
                    from(this.preferencesService.getTimezones(true) as Promise<ITimeZoneInfo[]>)
                ])
            }),
            tap(([profiles, timezones]) => {
                const profile = profiles.find(x => x.AccountId === accountId);

                this.deviceProfile = profile.CameraDeviceParameterProfileSettings;
                this.timezones = timezones;
                this.vehicleType = this.peripheral.vehicleMapping.vehicle.VehicleType;
            })
        ).subscribe();
    }

    private updateWakeUpCameraButtonLabel(): void {
        if (!this.deviceInfo?.dormantState || this.deviceInfo.dormantState === StreamaxDormantState.Unknown) {
            this.wakeUpCameraButtonLabel = this.translationService.t("FoundationPeripherals.CameraNotDormant");
            return;
        }

        this.wakeUpCameraButtonLabel = undefined;
    }

    private updateOpenCameraConfigButtonLabel(): void {
        if (!this.deviceInfo.IsOnline) {
            this.openCameraConfigButtonLabel = this.translationService.t('FoundationPeripherals.CameraIsOffline');
            return;
        }

        this.openCameraConfigButtonLabel = undefined;
    }

    private startRefreshingStatuses(): void {
        this.refreshStatusesTimer
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.refreshStatuses(false);
            });
    }
}
